import { ReactElement } from 'react';
import styled from 'styled-components';

import { caption, container, mediaQueries } from '@/theme/lib';
import Button from '../Button';
import usePagination from './usePagination';

const StyledContainer = styled.div`
  ${container}
  margin-top: 2rem;

  ${mediaQueries.lg} {
    margin-top: 8rem;
  }
`;

const StyledPagination = styled.ul`
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${mediaQueries.md} {
    gap: 0.75rem;
    justify-content: center;
  }
`;

const StyledNavLi = styled.li`
  list-style: none;

  &:first-child {
    margin-right: 2rem;
    button {
      white-space: nowrap;
    }
  }

  &:last-child {
    margin-left: 2rem;
    button {
      padding-left: 2rem;
      padding-right: 2rem;
      white-space: nowrap;
    }
  }

  button:disabled {
    opacity: 0.5;
    cursor: auto;
    pointer-events: none;
  }
`;

type StyledPageNumberProps = {
  isCurrent: boolean;
};
const StyledPageNumber = styled.li.withConfig({
  shouldForwardProp: (prop) => prop !== "isCurrent"
})<StyledPageNumberProps>`
  display: none;
  list-style: none;

  ${mediaQueries.md} {
    display: block;
  }

  button {
    ${caption}
    padding: 0.625rem 1rem;
    background: none;
    color: ${(props) => props.theme.colors.caption};
    border: ${(props) => (props.isCurrent ? `2px solid ${props.theme.colors.primary};` : 'none')};
    border-radius: 3px;
    font-weight: ${(props) => props.isCurrent && 700};
    cursor: pointer;
  }
`;

const StyledDots = styled.li`
  ${caption}
  color: ${(props) => props.theme.colors.caption};
  display: none;
  list-style: none;

  ${mediaQueries.md} {
    display: block;
  }
`;

type Props = {
  href: string;
  totalPages: number;
  //Current page use 1-based index
  currentPage: number;
  // onPageChange takes the page number that is changing into
  onPageChange: (href: string, pageNumber: number) => void;
  // Sibling count is the number of sibling pages we want to display on each side of the current page
  siblingCount?: number;
  className?: string;
};

export default function Pagination({
  href,
  totalPages,
  currentPage,
  onPageChange,
  siblingCount = 4,
  className,
}: Props): ReactElement<any> | null {
  const paginationRange = usePagination({
    totalPages,
    currentPage,
    siblingCount,
  });

  // Do not render this item if there's only one page
  if (totalPages <= 1) return null;

  const onNext = () => onPageChange(href, currentPage + 1);
  const onPrev = () => onPageChange(href, currentPage - 1);
  const lastPage = paginationRange[paginationRange.length - 1];

  return (
    <StyledContainer className={className}>
      <StyledPagination>
        <StyledNavLi>
          <Button as="button" disabled={currentPage === 1} onClick={onPrev} primary>
            Previous
          </Button>
        </StyledNavLi>
        {paginationRange.map((pageNumber, idx) => {
          if (pageNumber === 'dots') {
            return <StyledDots key={`dots${idx}`}>...</StyledDots>;
          }
          return (
            <StyledPageNumber key={pageNumber} isCurrent={currentPage === pageNumber}>
              <button onClick={() => onPageChange(href, pageNumber as number)}>{pageNumber}</button>
            </StyledPageNumber>
          );
        })}
        <StyledNavLi>
          <Button as="button" disabled={currentPage === lastPage} onClick={onNext} primary>
            Next
          </Button>
        </StyledNavLi>
      </StyledPagination>
    </StyledContainer>
  );
}
